import React, { useState } from 'react';
import { Alert, Button, Space, Spin, Tag, Typography, message } from 'antd';
import PageTabs from '../page/PageTabs';
import { RegulationDraftView, SourceLinkButton } from './RegulationViews';
import { EditModal, ReasonActionModal, ReviseModal, SupersedeModal } from './RegulationModals';
import RegulationAuditTrail from './RegulationAuditTrail';
import RegulationVersionDiff from './RegulationVersionDiff';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { userMessageForError } from '../../utils/apiError';
import { formatDdMmYyyy } from '../../utils/formatDdMmYyyy';
import { ADMIN_API, describeDismissError, topicTitleMap } from '../../utils/regulatoryAdminModel';
import {
    describeEnrichError,
    describePublicationError,
    documentTypeLabel,
    draftBlockReason,
} from '../../utils/regulatoryModel';

const { Text } = Typography;
const TEXT_PREVIEW_CHARS = 3000;
const STATUS_COLOR = { approved: 'green', rejected: 'red', pending: 'orange' };

/** Admin detail for one document: source text, Draft with AI, enrichment history, edit, supersede, withdraw, audit trail. */
const RegulationAdminDetail = ({ id, sources, onClose, onChanged }) => {
    const limits = sources?.limits;
    const topicTitles = topicTitleMap(sources?.taxonomy);
    const { request } = useApiRequest();
    const [rev, setRev] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [modal, setModal] = useState(null); // 'edit' | 'supersede' | 'unpublish' | 'republish' | 'dismiss' | 'restore' | 'clearSupersession' | 'revise'

    const detail = useResource(async () => (await request('GET', `/regulatory-content/${id}`)).data.item, [request, id, rev]);

    const refresh = async () => {
        setRev((r) => r + 1);
        await onChanged();
    };
    const done = () => { setModal(null); refresh(); };

    const draft = async () => {
        setBusy(true);
        setError(null);
        try {
            await request('POST', `/regulatory-content/enrich/${id}`);
            message.success('Draft created. Review it in the Review queue (a different admin must approve it).');
            await refresh();
        } catch (err) {
            setError(describeEnrichError(err));
            if (err?.status === 409) await refresh();
        } finally {
            setBusy(false);
        }
    };

    if (detail.status === 'loading' && !detail.data) return <Spin />;
    if (detail.status === 'error') {
        return <Alert type="error" message={userMessageForError(detail.error)} action={<Button onClick={detail.reload}>Retry</Button>} />;
    }
    const item = detail.data;
    if (!item) return null;

    const block = draftBlockReason(item, limits);
    const hasApproved = item.approvedVersions > 0;
    const published = hasApproved && !item.unpublished;
    const dismissed = !!item.dismissedAt;
    const text = item.text || '';
    const approvedVersions = (item.enrichments || []).filter((e) => e.status === 'approved');

    const details = (
        <div>
            {item.unpublished && (
                <Alert
                    type="warning"
                    style={{ marginBottom: 8 }}
                    data-testid="unpublished-note"
                    message={`Unpublished${item.unpublishedAt ? ` on ${formatDdMmYyyy(item.unpublishedAt)}` : ''}${item.unpublishedBy ? ` by ${item.unpublishedBy}` : ''}. Readers cannot see it. Reason: ${item.unpublishedReason || 'not recorded'}`}
                />
            )}
            {dismissed && <Alert type="info" style={{ marginBottom: 8 }} data-testid="dismissed-note" message={`Dismissed as not relevant${item.dismissedReason ? `. Reason: ${item.dismissedReason}` : ''}. It stays out of the work queue until restored.`} />}
            {item.pendingRevision && (
                <Alert
                    type="info"
                    style={{ marginBottom: 8 }}
                    data-testid="pending-revision"
                    message={`A source revision is pending (proposed ${formatDdMmYyyy(item.pendingRevision.proposedAt)}${item.pendingRevision.proposedBy ? ` by ${item.pendingRevision.proposedBy}` : ''}). Changed: ${(item.pendingRevision.changedFields || []).join(', ')}.`}
                    description={`Reason: ${item.pendingRevision.reason}. The published version stays live until a different admin approves a new draft made from this revision.`}
                    action={(
                        <Space>
                            <Button onClick={draft} disabled={busy || !!block}>Draft again</Button>
                            <Button onClick={() => setModal('cancelRevision')}>Cancel revision</Button>
                        </Space>
                    )}
                />
            )}
            <Text strong>Stored text ({text.length} characters)</Text>
            <pre style={{ maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap', background: 'var(--color-surface-sunken)', padding: 8 }} data-testid="regulation-text">
                {text.length > TEXT_PREVIEW_CHARS ? `${text.slice(0, TEXT_PREVIEW_CHARS)}\n[showing the first ${TEXT_PREVIEW_CHARS} of ${text.length} characters]` : text || 'No text stored.'}
            </pre>
            {!hasApproved && item.sourceName === 'sebi' && text.length < (limits?.minGroundingChars ?? 600) && (
                <Alert type="info" style={{ marginBottom: 8 }} message="SEBI's feed carries titles only. Edit the document and paste the official text before drafting." />
            )}

            <Space wrap style={{ margin: '8px 0' }}>
                <Button onClick={draft} disabled={!!block || busy || dismissed}>Draft with AI</Button>
                {!hasApproved && <Button onClick={() => setModal('edit')}>Edit</Button>}
                {hasApproved && !item.pendingRevision && <Button onClick={() => setModal('revise')}>Revise source</Button>}
                {published && <Button onClick={() => setModal('unpublish')}>Unpublish</Button>}
                {item.unpublished && <Button onClick={() => setModal('republish')}>Republish</Button>}
                {!item.supersededById && <Button onClick={() => setModal('supersede')}>Mark as superseded</Button>}
                {item.supersededById && <Button onClick={() => setModal('clearSupersession')}>Clear supersession</Button>}
                {!hasApproved && !dismissed && <Button onClick={() => setModal('dismiss')}>Dismiss</Button>}
                {dismissed && <Button onClick={() => setModal('restore')}>Restore</Button>}
            </Space>
            {block && !dismissed && <div><Text type="secondary" data-testid="draft-block-reason">{block}</Text></div>}
            {error && <Alert type="error" style={{ marginTop: 8 }} message={error} data-testid="detail-error" />}

            <RegulationVersionDiff versions={approvedVersions} />

            {(item.revisionHistory || []).length > 0 && (
                <div style={{ marginTop: 16 }} data-testid="revision-history">
                    <Text strong>Source revision history</Text>
                    {item.revisionHistory.map((h, i) => (
                        <div key={`${h.at || h.appliedAt || i}`}><Text type="secondary">{h.at || h.appliedAt ? `${formatDdMmYyyy(h.at || h.appliedAt)}: ` : ''}{h.reason || 'revised'}{Array.isArray(h.changedFields) && h.changedFields.length ? ` (${h.changedFields.join(', ')})` : ''}</Text></div>
                    ))}
                </div>
            )}

            <div style={{ marginTop: 16 }}>
                <Text strong>Draft history</Text>
                {(item.enrichments || []).length === 0 && <div><Text type="secondary">No AI drafts yet.</Text></div>}
                {(item.enrichments || []).map((e) => (
                    <div key={e.id} style={{ marginTop: 8 }} data-testid="enrichment-history-row">
                        <Space>
                            <Tag color={STATUS_COLOR[e.status]}>{e.status}</Tag>
                            <Text type="secondary">{formatDdMmYyyy(e.createdAt)}{e.reviewedBy ? `, reviewed by ${e.reviewedBy}` : ''}</Text>
                        </Space>
                        {e.rejectionReason && <div><Text type="secondary">Reason: {e.rejectionReason}</Text></div>}
                        {e.status === 'pending' && <div><Text type="secondary">Waiting in the Review queue.</Text></div>}
                        {e.status === 'approved' && <RegulationDraftView draft={e.draft} itServicesRelevance={e.itServicesRelevance} reviewed triggeredBy={e.triggeredBy} topicTitles={topicTitles} />}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, marginTop: 16 }} data-testid="admin-detail">
            <Space style={{ marginBottom: 8 }}>
                <Tag>{item.authorityName}</Tag>
                <Tag>{documentTypeLabel(item.documentType)}</Tag>
                {published ? <Tag color="green">Published v{item.approvedVersions}</Tag> : <Tag>Unpublished</Tag>}
                {item.supersededById && <Tag color="red">Superseded</Tag>}
                {(item.topics || []).map((t) => <Tag key={t.id} data-testid="topic-tag">{t.title}</Tag>)}
                <Button onClick={onClose}>Close</Button>
            </Space>
            <div><Text strong>{item.title}</Text></div>
            <div>
                <Text type="secondary">
                    {item.officialDocumentNumber || 'No reference number'} · Issued {formatDdMmYyyy(item.publishedAt)}
                    {item.effectiveDate ? ` · Effective ${formatDdMmYyyy(item.effectiveDate)}` : ''}
                </Text>
            </div>
            <div style={{ margin: '8px 0' }}><SourceLinkButton url={item.sourceUrl} /></div>

            <PageTabs
                defaultActiveKey="details"
                items={[
                    { key: 'details', label: 'Details', children: details },
                    { key: 'audit', label: 'Audit trail', children: <RegulationAuditTrail id={id} /> },
                ]}
            />

            {modal === 'edit' && <EditModal item={item} sources={sources} onClose={() => setModal(null)} onDone={done} />}
            {modal === 'revise' && <ReviseModal item={item} sources={sources} onClose={() => setModal(null)} onDone={done} />}
            {modal === 'supersede' && <SupersedeModal item={item} onClose={() => setModal(null)} onDone={done} />}
            {(modal === 'unpublish' || modal === 'republish') && (
                <ReasonActionModal
                    title={modal === 'unpublish' ? 'Unpublish this regulation?' : 'Republish this regulation?'}
                    okText={modal === 'unpublish' ? 'Unpublish' : 'Republish'}
                    description={modal === 'unpublish' ? 'Readers stop seeing it immediately. Nothing is deleted, and you can republish it later.' : 'Readers will see the last approved summary again.'}
                    ariaLabel="Publication reason"
                    path={`/regulatory-content/${item.id}/${modal}`}
                    successMessage={modal === 'unpublish' ? 'Unpublished. Readers no longer see it.' : 'Republished. Readers can see it again.'}
                    describeError={describePublicationError}
                    breakGlassAvailable={modal === 'republish' && sources?.breakGlassAvailable === true}
                    onClose={() => setModal(null)}
                    onDone={done}
                />
            )}
            {modal === 'dismiss' && (
                <ReasonActionModal title="Dismiss this document?" okText="Dismiss" description="Use this for feed items that are not relevant. It leaves the work queue and can be restored later." ariaLabel="Dismissal reason" path={ADMIN_API.dismiss(item.id)} describeError={describeDismissError} successMessage="Dismissed." onClose={() => setModal(null)} onDone={done} />
            )}
            {modal === 'restore' && (
                <ReasonActionModal title="Restore this document?" okText="Restore" description="It returns to the work queue." ariaLabel="Restore reason" path={ADMIN_API.restore(item.id)} describeError={describeDismissError} successMessage="Restored." onClose={() => setModal(null)} onDone={done} />
            )}
            {modal === 'cancelRevision' && (
                <ReasonActionModal title="Cancel the pending revision?" okText="Cancel revision" description="The published version is unchanged. The revision is discarded." ariaLabel="Cancel revision reason" path={ADMIN_API.reviseCancel(item.id)} successMessage="Revision cancelled." onClose={() => setModal(null)} onDone={done} />
            )}
            {modal === 'clearSupersession' && (
                <ReasonActionModal title="Clear supersession?" okText="Clear supersession" description="The document is treated as current again." ariaLabel="Clear supersession reason" path={ADMIN_API.clearSupersession(item.id)} successMessage="Supersession cleared." onClose={() => setModal(null)} onDone={done} />
            )}
        </div>
    );
};

export default RegulationAdminDetail;
